import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  TextField,
  Typography,
  Box,
  Link as MuiLink,
  Chip,
} from '@mui/material';
import BridgeBankLogo from './BridgeBankLogo';
import { ThemeProvider } from '@mui/material/styles';
import lightTheme from '../theme';
import { AppDispatch } from '../store';
import { login } from '../store/authSlice';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
import usersService from '../services/usersService';

const LoginPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('admin@lms.com');
  const [password, setPassword] = useState('password');
  const [merchantId, setMerchantId] = useState('');

  // Function to determine role from email
  const determineRole = (email: string): 'merchant' | 'wave' | 'bank' => {
    if (email.includes('admin@lms.com')) return 'bank';
    if (email.includes('wave@lms.com')) return 'wave';
    if (email.includes('merchant@lms.com')) return 'merchant';
    return 'bank'; // Default fallback
  };

  // Get role based on current email
  const currentRole = useMemo(() => determineRole(email), [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let privileges: string[] | undefined;
    try {
      const userRecord = await usersService.getByEmail(email);
      privileges = userRecord?.privileges ?? undefined;
    } catch {
      privileges = undefined;
    }
    dispatch(login({
      email,
      role: currentRole,
      merchantId: currentRole === 'merchant' ? merchantId || 'merchant-001' : undefined,
      privileges,
    }));
    navigate('/');
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(21, 101, 192, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(0, 172, 193, 0.12) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(13, 71, 161, 0.08) 0%, transparent 50%)
            `,
            pointerEvents: 'none'
          }}
        />
        
        {/* Main Login Card */}
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            padding: 4,
            width: '100%',
            maxWidth: 450,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            zIndex: 1
          }}
        >
          {/* Logo Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                borderRadius: 3,
                py: 2,
                px: 3,
                boxShadow: '0 8px 24px rgba(21, 101, 192, 0.3)',
              }}
            >
              <BridgeBankLogo height={60} />
            </Box>
            <Typography 
              component="h1" 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: '#1e293b',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                mb: 1
              }}
            >
              {t('login.creditScoringEngine')}
            </Typography>
            <Typography 
              component="p" 
              variant="subtitle1" 
              sx={{ 
                color: '#64748b',
                fontWeight: 500,
                fontSize: '1.1rem'
              }}
            >
              {currentRole === 'bank' ? t('login.bridgeBankPortal') : currentRole === 'wave' ? t('login.wavePortal') : t('login.merchantPortal')}
            </Typography>
          </Box>

          {/* Role Indicator */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Chip 
              label={`${currentRole === 'bank' ? t('login.bridgeBankAccess') : currentRole === 'wave' ? t('login.waveAccess') : t('login.merchantAccess')}`}
              color={currentRole === 'bank' ? 'primary' : currentRole === 'wave' ? 'secondary' : 'default'}
              variant="filled"
              sx={{ 
                fontWeight: 600,
                fontSize: '0.9rem',
                py: 1,
                px: 2,
                background: currentRole === 'bank' 
                  ? 'linear-gradient(135deg, #1976d2, #1565c0)' 
                  : currentRole === 'wave' 
                  ? 'linear-gradient(135deg, #9c27b0, #7b1fa2)' 
                  : 'linear-gradient(135deg, #757575, #616161)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            />
          </Box>

          {/* Form */}
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('login.emailAddress')}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: '#1565c0' },
                  '&.Mui-focused fieldset': { borderColor: '#1565c0' },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#1565c0' },
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('login.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': { borderColor: '#1565c0' },
                  '&.Mui-focused fieldset': { borderColor: '#1565c0' },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#1565c0' },
              }}
            />
            
            {/* Merchant ID Field */}
            {currentRole === 'merchant' && (
              <TextField
                margin="normal"
                fullWidth
                id="merchantId"
                label={t('settings.merchantId')}
                name="merchantId"
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#1565c0' },
                    '&.Mui-focused fieldset': { borderColor: '#1565c0' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#1565c0' },
                }}
              />
            )}
            
            {/* Sign In Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 4, 
                mb: 2, 
                py: 1.5,
                background: 'linear-gradient(135deg, #1565c0, #0d47a1)',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 8px 25px rgba(21, 101, 192, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0d47a1, #0a3880)',
                  boxShadow: '0 12px 35px rgba(21, 101, 192, 0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('login.signIn')}
            </Button>
            
            {/* Forgot Password Link */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <MuiLink 
                href="#" 
                variant="body2"
                sx={{ 
                  color: '#64748b',
                  textDecoration: 'none',
                  '&:hover': {
                    color: '#1565c0',
                    textDecoration: 'underline'
                  },
                  transition: 'color 0.3s ease'
                }}
              >
                {t('login.forgotPassword')}
              </MuiLink>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default LoginPage; 